// Pure carousel wire-JSON helpers. No React. See ../README.md for the swap-out plan — this whole
// folder is temporary, standing in for @gs/gs-integration-component's own carousel panel.
//
// carouselCards is the raw list-row shape (helper/utils.js's mapTemplate leaves it untouched via
// its `...template` spread — details.carouselCards): [{ components: [
//   { type: 'header', format: 'IMAGE'|'VIDEO', ... },
//   { type: 'body', text: '... {{1}} ...' },
//   { type: 'buttons', buttons: [{ type, text, urlType?, url? }] },
// ] }].
//
// values is this folder's own in-memory shape, built/consumed by CarouselSendPanel:
//   { cards: [ { media: string, body: string[], buttons: { [buttonIndex]: string } } ] }
import { countBraceTokens } from '../shared/validators';
import { findComponent } from '../shared/savedJsonMatch';

// Pending a live send test against Enterprise. 'library' matches @gs/gs-integration-component's
// own carousel output exactly, so switching CAROUSEL_WIRE_SHAPE back to it after the real swap
// (deleting this folder) changes nothing downstream.
export const CAROUSEL_WIRE_SHAPE = 'library';

// Temporary test switch: set with localStorage.setItem('br_carousel_wire_shape','meta') in the
// widget iframe's console, then re-save the node. Remove once the live test settles the default.
export function activeCarouselWireShape() {
  try {
    // localStorage can throw in sandboxed iframes — fall back to the constant.
    const override = window.localStorage.getItem('br_carousel_wire_shape');
    if (override === 'library' || override === 'meta') return override;
  } catch {
    // fall through to the constant below
  }
  return CAROUSEL_WIRE_SHAPE;
}

// Builds the { components: [{ type: 'CAROUSEL', cards: [...] }] } wire object from the template's
// raw carouselCards plus the typed-in values, in either shape (see CAROUSEL_WIRE_SHAPE above).
export function buildCarouselTemplateJson(carouselCards, values, shape = CAROUSEL_WIRE_SHAPE) {
  const cardsInput = Array.isArray(carouselCards) ? carouselCards : [];
  const valueCards = Array.isArray(values?.cards) ? values.cards : [];
  const isMeta = shape === 'meta';

  const cards = cardsInput.map((card, cardIndex) => {
    const components = Array.isArray(card?.components) ? card.components : [];
    const cardValues = valueCards[cardIndex] || {};
    const outComponents = [];

    const headerComponent = findComponent(components, 'header');
    if (headerComponent) {
      const fmt = String(headerComponent.format || '').toLowerCase();
      outComponents.push({
        type: 'header',
        parameters: [{ type: fmt, [fmt]: { link: cardValues.media || '' } }],
      });
    }

    const bodyComponent = findComponent(components, 'body');
    if (bodyComponent) {
      const count = countBraceTokens(bodyComponent.text);
      const bodyValues = Array.isArray(cardValues.body) ? cardValues.body : [];
      const parameters = [];
      for (let i = 1; i <= count; i += 1) {
        parameters.push({ type: 'text', text: bodyValues[i - 1] || '' });
      }
      // library: body is always present, even with zero parameters. meta: an empty body is
      // omitted entirely.
      if (!isMeta || parameters.length > 0) {
        outComponents.push({ type: 'body', parameters });
      }
    }

    const buttonsComponent = findComponent(components, 'buttons');
    const buttons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
    buttons.forEach((button, buttonIndex) => {
      const isDynamicUrl = button?.type === 'URL' && button?.urlType !== 'STATIC';
      if (isDynamicUrl) {
        const suffix = (cardValues.buttons && cardValues.buttons[buttonIndex]) || '';
        outComponents.push(isMeta
          ? { type: 'button', sub_type: 'url', index: buttonIndex, parameters: [{ type: 'text', text: suffix }] }
          : { type: 'button', index: buttonIndex, sub_type: button.type, parameters: [{ type: 'payload', payload: suffix }] });
      } else if (isMeta && button?.type === 'QUICK_REPLY') {
        // Meta's documented shape includes quick replies; the library's own output omits them.
        outComponents.push({
          type: 'button',
          sub_type: 'quick_reply',
          index: buttonIndex,
          parameters: [{ type: 'payload', payload: button.text || '' }],
        });
      }
      // Static URL / phone-number buttons emit nothing in either shape — nothing for Enterprise
      // to substitute, same as the library.
    });

    return { card_index: cardIndex, components: outComponents };
  });

  return { components: [{ type: 'CAROUSEL', cards }] };
}

// Reverse of buildCarouselTemplateJson, for restore. Reads either shape. Always returns an array
// sized to carouselCards — blank ('') entries where the saved JSON had nothing at that position,
// which is also templateDrift.js's own drift signal.
export function parseCarouselTemplateJson(waTemplateJson, carouselCards) {
  const cardsInput = Array.isArray(carouselCards) ? carouselCards : [];
  // Body slots are sized: the panel fills a card's variable by mapping over this array, so an empty one cannot be typed into.
  const blank = () => ({
    cards: cardsInput.map((card) => ({
      media: '',
      body: Array.from({ length: countBraceTokens(findComponent(card?.components, 'body')?.text) }, () => ''),
      buttons: {},
    })),
  });

  try {
    const carouselComponent = (waTemplateJson?.components || []).find((component) => component?.type === 'CAROUSEL');
    const wireCards = carouselComponent?.cards;
    if (!Array.isArray(wireCards)) return blank();

    return {
      cards: cardsInput.map((card, cardIndex) => {
        const components = Array.isArray(card?.components) ? card.components : [];
        const wireCard = wireCards.find((c) => c?.card_index === cardIndex) || wireCards[cardIndex];
        const wireComponents = Array.isArray(wireCard?.components) ? wireCard.components : [];

        let media = '';
        const body = [];
        const buttons = {};

        const headerComponent = findComponent(components, 'header');
        if (headerComponent) {
          const fmt = String(headerComponent.format || '').toLowerCase();
          const wireHeader = findComponent(wireComponents, 'header');
          const param = wireHeader?.parameters?.[0];
          media = param?.[fmt]?.link || (param?.type ? param?.[param.type]?.link : '') || '';
        }

        const bodyComponent = findComponent(components, 'body');
        if (bodyComponent) {
          const count = countBraceTokens(bodyComponent.text);
          const wireBody = findComponent(wireComponents, 'body');
          const params = Array.isArray(wireBody?.parameters) ? wireBody.parameters : [];
          for (let i = 0; i < count; i += 1) {
            body.push(params[i]?.text || '');
          }
        }

        const buttonsComponent = findComponent(components, 'buttons');
        const cardButtons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
        cardButtons.forEach((button, buttonIndex) => {
          if (button?.type === 'URL' && button?.urlType !== 'STATIC') {
            const wireButton = wireComponents.find((c) => c?.type === 'button'
              && c?.index === buttonIndex
              && String(c?.sub_type || '').toUpperCase() === 'URL');
            const param = wireButton?.parameters?.[0];
            buttons[buttonIndex] = (param?.payload ?? param?.text ?? '') || '';
          }
        });

        return { media, body, buttons };
      }),
    };
  } catch {
    return blank();
  }
}

// Vendored from @gs/gs-integration-component's Helper/Utils.tsx (buildCarouselVariables,
// ~374-396) — not part of the package's public exports. Turns a built waTemplateJson's cards
// into the { mediaURL, variables[] } shape gs-whatsapp-template-preview reads as card_variables,
// reading each parameter generically (param[param.type]) so it works against either wire shape.
export function buildCarouselVariables(cards) {
  if (!cards) return [];
  return cards.map((card) => {
    const cardVar = { mediaURL: '', variables: [] };
    (card?.components || []).forEach((comp) => {
      const parameter = comp?.parameters?.[0];
      if (comp?.type === 'header') {
        cardVar.mediaURL = parameter?.[parameter?.type]?.link;
      } else if (comp?.type === 'body') {
        (comp?.parameters || []).forEach((param) => {
          cardVar.variables.push(param?.[param?.type] || '');
        });
      } else if (comp?.type === 'button') {
        cardVar.variables.push(parameter?.[parameter?.type] || '');
      }
    });
    return cardVar;
  });
}
